import '../../tables/era-eob-charges/neb-table-other-payments';

import { html, css } from 'lit';

import {
  getRecoupByPaymentAllocationId,
  recoupAllocation,
  removeRecoupHistory,
} from '../../../../packages/neb-api-client/src/allocation-api-client';
import { openError } from '../../../../packages/neb-dialog/neb-banner-state';
import { BUTTON_ROLE } from '../../../../packages/neb-lit-components/src/components/neb-button';
import {
  openOverlay,
  OVERLAY_KEYS,
} from '../../../../packages/neb-lit-components/src/utils/overlay-constants';
import {
  DIRTY_POPUP_MODEL,
  openDirtyPopup,
} from '../../../../packages/neb-popup';
import NebPopup, {
  ELEMENTS as BASE_ELEMENTS,
} from '../../../../packages/neb-popup/src/neb-popup';
import { store } from '../../../../packages/neb-redux/neb-redux-store';
import { centsToCurrency } from '../../../../packages/neb-utils/formatters';
import * as eraEobApi from '../../../api-clients/era-eob';
import { CSS_FONT_FAMILY, CSS_SPACING } from '../../../styles';
import { DIRTY_POPUP_MODEL_PROCEED } from '../../forms/era-eob/neb-form-era-eob-management';

export const ELEMENTS = {
  ...BASE_ELEMENTS,
  paymentsTable: { id: 'payments-table' },
  buttonCancel: { id: 'cancel' },
};

const TABLE_CONFIG = [
  {
    key: 'paymentNumber',
    label: 'Payment ID',
    flex: css`1 0 0`,
  },
  {
    key: 'amount',
    label: 'Payment Amount',
    flex: css`1 0 0`,
    formatter: v => centsToCurrency(v),
  },
  {
    key: 'allocationAmount',
    label: 'Payment Allocated',
    flex: css`1 0 0`,
    formatter: (v, item) =>
      item.isAllocationValid ? centsToCurrency(v) : centsToCurrency(0),
  },
];

class NebPopupOtherPayments extends NebPopup {
  initState() {
    super.initState();
    this.model = {
      title: '',
      payments: [],
      paymentId: '',
    };
  }

  initHandlers() {
    this.handlers = {
      cancel: () => {
        this.onClose(this.model.payments);
      },
      keydown: e => {
        if (e.key === 'Escape') {
          this.onClose(this.model.payments);
        }
      },
      clickPaymentLink: async rowIndex => {
        if (this.model.dirty && !(await openDirtyPopup(DIRTY_POPUP_MODEL))) {
          return;
        }

        const payment = this.model.payments[rowIndex];
        const id = payment.eobId ? payment.eobId : payment.eraId;
        const type = payment.eobId ? 'EOB' : 'ERA';

        try {
          const { data } = await eraEobApi.getSummaryById({
            id,
            type,
          });

          if (data.length) {
            await Promise.all([
              openOverlay(OVERLAY_KEYS.ERA_EOB_MANAGEMENT, {
                readonly: false,
                era: data[0],
              }),
              Promise.resolve(this.onClose(false)),
            ]);

            window.dispatchEvent(
              new CustomEvent('neb-refetch-other-payments', {
                bubbles: true,
                composed: true,
              }),
            );
          }
        } catch (error) {
          console.error(error);
          store.dispatch(openError(`${type} not found.`));
        }
      },
      recoup: async ({ allocationId, paymentId, rowIndex }) => {
        try {
          if (
            this.model.dirty &&
            !(await openDirtyPopup(DIRTY_POPUP_MODEL_PROCEED))
          ) {
            return;
          }

          await recoupAllocation(this.model.paymentId, paymentId, allocationId);

          const recoup = await getRecoupByPaymentAllocationId(
            paymentId,
            allocationId,
          );

          this.model.payments[rowIndex] = {
            ...this.model.payments[rowIndex],
            recoupId: recoup.id,
            isAllocationValid: false,
          };

          this.model.payments = [...this.model.payments];

          this.requestUpdate();
        } catch (error) {
          console.error(error);
          store.dispatch(openError('Failed to recoup allocation.'));
        }
      },
      removeRecoupHistory: async ({
        paymentId,
        allocationId,
        recoupId,
        rowIndex,
      }) => {
        try {
          await removeRecoupHistory(allocationId, paymentId, recoupId);

          this.model.payments.splice(rowIndex, 1);
          this.model.payments = [...this.model.payments];

          this.requestUpdate();
        } catch (error) {
          console.error(error);
          store.dispatch(openError('Failed to remove recoup history.'));
        }
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.handlers.keydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.handlers.keydown);
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          font-family: ${CSS_FONT_FAMILY};
          width: 745px;
        }

        .content {
          margin-top: 0;
        }

        .relative {
          position: relative;
        }

        .container-buttons {
          margin-top: ${CSS_SPACING};
          display: flex;
        }

        .table {
          padding-top: ${CSS_SPACING};
        }
      `,
    ];
  }

  modelChanged() {
    this.title = this.model.title;
  }

  __renderTable() {
    return html`
      <neb-table-other-payments
        id="${ELEMENTS.paymentsTable.id}"
        class="table"
        .config="${TABLE_CONFIG}"
        .model="${this.model.payments}"
        .onPaymentLinkClick="${this.handlers.clickPaymentLink}"
        .onRecoup="${this.handlers.recoup}"
        .onRemoveRecoupHistory="${this.handlers.removeRecoupHistory}"
      ></neb-table-other-payments>
    `;
  }

  __renderButtons() {
    return html`
      <div class="container-buttons">
        <neb-button
          id="${ELEMENTS.buttonCancel.id}"
          role="${BUTTON_ROLE.CANCEL}"
          label="Done"
          .onClick="${this.handlers.cancel}"
        ></neb-button>
      </div>
    `;
  }

  renderContent() {
    return html` ${this.__renderTable()} ${this.__renderButtons()} `;
  }

  render() {
    return html`
      <div class="container relative">
        ${this.renderHeader()}
        <div class="content">${this.renderContent()}</div>
      </div>
    `;
  }
}

customElements.define('neb-popup-other-payments', NebPopupOtherPayments);
